import { useMemo, useState } from 'react';
import { cn } from 'lib/cn';
import { Card, CardHeader, Input, TextArea, Select, Button } from 'components/ui';
import { ImageUploadField } from 'components/ImageUploadField';
import { buildVendorProfileInput } from '../utils/shopProfile';
import { useShopEnums, languageEnumValue } from '../hooks/useShopEnums';

const TOGGLES = [
  { key: 'isOnline', label: 'Shop is online', hint: 'Customers can place orders.' },
  { key: 'autoAcceptOrders', label: 'Auto-accept orders', hint: 'Skip manual acceptance.' },
  { key: 'acceptsNotifications', label: 'Accept notifications', hint: 'Receive order alerts.' },
  { key: 'isUseOtp', label: 'Require OTP on delivery', hint: 'Verify handover with a code.' },
];

const sameValue = (a, b) => String(a).toUpperCase() === String(b).toUpperCase();

/** Multi-select pill group. */
function ChipMulti({ options, selected, onToggle }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const active = selected.some((value) => sameValue(value, option.value));
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onToggle(option.value)}
            className={cn(
              'rounded-full px-3 py-1.5 text-sm font-medium transition',
              active
                ? 'bg-brand-600 text-white'
                : 'bg-white text-slate-600 ring-1 ring-inset ring-surface-300 hover:text-slate-900',
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Reusable shop (vendor profile) form. Manages its own state from
 * `initialValues`; key it on the shop id so it resets when the source changes.
 */
export function ShopForm({ initialValues, onSubmit, submitting, submitLabel, isNew = false }) {
  const [form, setForm] = useState(() => initialValues);

  const { vendorTypes, businessTypes, days, languages } = useShopEnums(form.vendorType);

  const vendorTypeOptions = useMemo(
    () => vendorTypes.map((type) => ({ value: type.value, label: type.displayValue })),
    [vendorTypes],
  );
  const languageOptions = useMemo(
    () => languages.map((lang) => ({ value: languageEnumValue(lang), label: lang.displayName })),
    [languages],
  );

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const setVendorType = (value) =>
    setForm((prev) => ({ ...prev, vendorType: value, businessType: [] }));

  const toggleInArray = (field) => (value) =>
    setForm((prev) => {
      const exists = prev[field].some((entry) => sameValue(entry, value));
      return {
        ...prev,
        [field]: exists
          ? prev[field].filter((entry) => !sameValue(entry, value))
          : [...prev[field], value],
      };
    });

  const toggleAllDays = () =>
    setForm((prev) => ({
      ...prev,
      daysActive: prev.daysActive.length === days.length ? [] : days.map((day) => day.value),
    }));

  const handleSubmit = (event) => {
    event.preventDefault();
    onSubmit(buildVendorProfileInput(form, { isNew }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Card>
        <CardHeader title="Business details" />
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Business name"
            required={isNew}
            containerClassName="sm:col-span-2"
            value={form.businessName}
            onChange={(event) => setField('businessName', event.target.value)}
          />
          <Input
            label="Business email"
            type="email"
            value={form.businessEmail}
            onChange={(event) => setField('businessEmail', event.target.value)}
          />
          <Input
            label="Business phone"
            type="tel"
            value={form.businessPhone}
            onChange={(event) => setField('businessPhone', event.target.value)}
          />
          <TextArea
            label="Description"
            containerClassName="sm:col-span-2"
            value={form.businessDescription}
            onChange={(event) => setField('businessDescription', event.target.value)}
          />
        </div>
      </Card>

      <Card>
        <CardHeader title="Business type" subtitle="What you sell — helps customers find you." />
        <div className="space-y-4">
          <Select
            label="Vendor type"
            placeholder="Select a vendor type"
            options={vendorTypeOptions}
            value={form.vendorType}
            onChange={(event) => setVendorType(event.target.value)}
          />
          {form.vendorType && (
            <div>
              <span className="mb-1.5 block text-sm font-medium text-slate-700">Business types</span>
              {businessTypes.length > 0 ? (
                <ChipMulti
                  options={businessTypes.map((type) => ({
                    value: type.value,
                    label: type.displayValue,
                  }))}
                  selected={form.businessType}
                  onToggle={toggleInArray('businessType')}
                />
              ) : (
                <p className="text-sm text-slate-400">No business types for this vendor type.</p>
              )}
            </div>
          )}
        </div>
      </Card>

      <Card>
        <CardHeader title="Address" />
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Street line 1"
            containerClassName="sm:col-span-2"
            value={form.street1}
            onChange={(event) => setField('street1', event.target.value)}
          />
          <Input
            label="Street line 2"
            containerClassName="sm:col-span-2"
            value={form.street2}
            onChange={(event) => setField('street2', event.target.value)}
          />
          <Input label="City" value={form.city} onChange={(event) => setField('city', event.target.value)} />
          <Input label="State" value={form.state} onChange={(event) => setField('state', event.target.value)} />
          <Input
            label="Postal code"
            value={form.postalCode}
            onChange={(event) => setField('postalCode', event.target.value)}
          />
          <Input
            label="Country"
            value={form.country}
            onChange={(event) => setField('country', event.target.value)}
          />
        </div>
      </Card>

      <Card>
        <CardHeader title="Hours & open days" />
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Opening time"
            placeholder="e.g. 09:00"
            value={form.openTime}
            onChange={(event) => setField('openTime', event.target.value)}
          />
          <Input
            label="Closing time"
            placeholder="e.g. 21:00"
            value={form.closeTime}
            onChange={(event) => setField('closeTime', event.target.value)}
          />
        </div>
        {days.length > 0 && (
          <div className="mt-4">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Open days</span>
              <button
                type="button"
                onClick={toggleAllDays}
                className="text-xs font-medium text-brand-700 hover:underline"
              >
                {form.daysActive.length === days.length ? 'Clear all' : 'Select all'}
              </button>
            </div>
            <ChipMulti
              options={days.map((day) => ({ value: day.value, label: day.displayValue }))}
              selected={form.daysActive}
              onToggle={toggleInArray('daysActive')}
            />
          </div>
        )}
      </Card>

      <Card>
        <CardHeader title="Compliance" />
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="GST number"
            value={form.gstRegistrationNumber}
            onChange={(event) => setField('gstRegistrationNumber', event.target.value)}
          />
          <Input
            label="FSSAI license number"
            value={form.fssaiLicenseNumber}
            onChange={(event) => setField('fssaiLicenseNumber', event.target.value)}
          />
        </div>
      </Card>

      <Card>
        <CardHeader title="Photo & documents" subtitle="Shop logo, business license and menu." />
        <div className="grid gap-4 sm:grid-cols-3">
          <ImageUploadField
            label="Shop logo / photo"
            value={form.vendorProfilePic}
            onChange={(value) => setField('vendorProfilePic', value)}
          />
          <ImageUploadField
            label="Business license"
            value={form.licenseUrl}
            onChange={(value) => setField('licenseUrl', value)}
          />
          <ImageUploadField
            label="Menu"
            value={form.menuUrl}
            onChange={(value) => setField('menuUrl', value)}
          />
        </div>
      </Card>

      <Card>
        <CardHeader title="Preferences" />
        <div className="space-y-4">
          {languageOptions.length > 0 && (
            <Select
              label="Preferred language"
              placeholder="Select a language"
              options={languageOptions}
              value={form.preferredLanguage}
              onChange={(event) => setField('preferredLanguage', event.target.value)}
              containerClassName="sm:max-w-xs"
            />
          )}
          {TOGGLES.map((toggle) => (
            <div key={toggle.key} className="flex items-start justify-between gap-4">
              <label htmlFor={`pref-${toggle.key}`} className="cursor-pointer">
                <span className="block text-sm font-medium text-slate-800">{toggle.label}</span>
                <span className="block text-xs text-slate-500">{toggle.hint}</span>
              </label>
              <input
                id={`pref-${toggle.key}`}
                type="checkbox"
                checked={form[toggle.key]}
                onChange={(event) => setField(toggle.key, event.target.checked)}
                className="mt-1 h-4 w-4 shrink-0 rounded border-surface-300 text-brand-600 focus:ring-brand-500"
              />
            </div>
          ))}
        </div>
      </Card>

      <div className="flex justify-end">
        <Button type="submit" size="lg" isLoading={submitting}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

export default ShopForm;
